export type SignupErrorKey =
	| "emailInUse"
	| "invalidEmail"
	| "networkError"
	| "signupFailed"
	| "tooManyAttempts"
	| "weakPassword";

export type ResetErrorKey = "invalidEmail" | "networkError" | "resetFailed" | "tooManyAttempts";
export type ResendErrorKey = "cooldown" | "resendFailed";
export type SocialErrorKey = "accountExists" | "networkError" | "popupBlocked" | "socialFailed";

/** Firebase's `auth/...` code, or the message when an error carries none. */
function reasonOf(error: unknown): string {
	if (!(error instanceof Error)) return "";
	const code = "code" in error && typeof error.code === "string" ? error.code : "";
	return code || error.message;
}

/** Maps a Firebase failure to the `signup.*` message that explains it. */
export function signupErrorKey(error: unknown): SignupErrorKey {
	const reason = reasonOf(error);
	if (reason.includes("email-already-in-use")) return "emailInUse";
	if (reason.includes("weak-password")) return "weakPassword";
	if (reason.includes("invalid-email")) return "invalidEmail";
	if (reason.includes("too-many-requests")) return "tooManyAttempts";
	if (reason.includes("network-request-failed")) return "networkError";
	return "signupFailed";
}

/**
 * A password reset answers the same whether or not the address has an account, so an unknown
 * address is `null` (shown as success), never a message that confirms who is registered.
 */
export function resetErrorKey(error: unknown): ResetErrorKey | null {
	const reason = reasonOf(error);
	if (reason.includes("user-not-found")) return null;
	if (reason.includes("invalid-email")) return "invalidEmail";
	if (reason.includes("too-many-requests")) return "tooManyAttempts";
	if (reason.includes("network-request-failed")) return "networkError";
	return "resetFailed";
}

/** Firebase rate-limits verification mails: that is a pause to wait out, not a failure. */
export function resendErrorKey(error: unknown): ResendErrorKey {
	return reasonOf(error).includes("too-many-requests") ? "cooldown" : "resendFailed";
}

/** Google and Apple popups: closing the popup is a choice, so it says nothing (`null`). */
export function socialErrorKey(error: unknown): SocialErrorKey | null {
	const reason = reasonOf(error);
	if (reason.includes("popup-closed-by-user") || reason.includes("cancelled-popup-request")) {
		return null;
	}
	if (reason.includes("account-exists-with-different-credential")) return "accountExists";
	if (reason.includes("popup-blocked")) return "popupBlocked";
	if (reason.includes("network-request-failed")) return "networkError";
	return "socialFailed";
}
